import { Factory, Tags, Target, Truck, type LucideIcon } from 'lucide-react'
import { z } from 'zod'
import { defaultForecastRange } from '../previsions-produits/-search-schema'

export const MEASURES = ['uvc', 'ca', 'tonnage'] as const
export const SEGMENTS = ['uf', 'commercial', 'industriel', 'reappro'] as const

export type Measure = (typeof MEASURES)[number]
export type Segment = (typeof SEGMENTS)[number]

export const previsionsSearchSchema = z.object({
  storeIds: z.array(z.number().int().positive()).optional().default([]),
  startDate: z.iso.date().optional().catch(undefined),
  endDate: z.iso.date().optional().catch(undefined),
  categoryIds: z.array(z.number().int().nonnegative()).optional().default([]),
  kitchenIds: z.array(z.number().int().positive()).optional().default([]),
  measure: z.enum(MEASURES).optional().default('tonnage'),
  segment: z.enum(SEGMENTS).optional().default('commercial'),
  capping: z.boolean().optional().default(false),
  focusDate: z.iso.date().optional().catch(undefined),
})

export type PrevisionsSearch = z.infer<typeof previsionsSearchSchema>

export interface PrevisionsRange {
  start: string
  end: string
}

export const resolvePrevisionsRange = (search: PrevisionsSearch): PrevisionsRange => {
  const fallback = defaultForecastRange()
  return {
    start: search.startDate ?? fallback.start,
    end: search.endDate ?? fallback.end,
  }
}

export const isDateInRange = (date: string, range: PrevisionsRange): boolean =>
  date >= range.start && date <= range.end

export const MEASURE_LABELS: Record<Measure, string> = {
  uvc: 'UVC',
  ca: 'CA',
  tonnage: 'Tonnage',
}

export const SEGMENT_LABELS: Record<Segment, string> = {
  uf: 'UF / Negoce',
  commercial: 'Commercial',
  industriel: 'Industriel',
  reappro: 'Réappro',
}

export const SEGMENT_ICONS: Record<Segment, LucideIcon> = {
  uf: Target,
  commercial: Tags,
  industriel: Factory,
  reappro: Truck,
}

export type CategorySource = 'commercial' | 'internal' | 'reappro'

export const SEGMENT_CATEGORY_SOURCE: Record<Segment, CategorySource> = {
  uf: 'commercial',
  commercial: 'commercial',
  industriel: 'internal',
  reappro: 'reappro',
}

/** Axe de regroupement attendu par l'endpoint des séries journalières. */
export const SEGMENT_GROUP_BY: Record<Segment, 'uf' | 'category' | 'internal' | 'reappro'> = {
  uf: 'uf',
  commercial: 'category',
  industriel: 'internal',
  reappro: 'reappro',
}

export type AxisGroupBy = (typeof SEGMENT_GROUP_BY)[Segment]

export const PREVISIONS_EXPORT_GROUP_BYS: AxisGroupBy[] = SEGMENTS.map(
  (segment) => SEGMENT_GROUP_BY[segment],
)
